import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { Sparkles, Gift, ArrowRight, Lock } from "lucide-react";
import { api } from "@/lib/apiClient";
import { memberApi } from "@/lib/memberClient";
import { useMember } from "@/contexts/MemberContext";
import LoyaltyTrail from "@/components/LoyaltyTrail";

/**
 * Homepage flagship — Popsicle Loyalty Journey.
 * Guests see the concept & CTA; signed-in members see their current cycle.
 */
export default function LoyaltyJourneyShowcase() {
  const sectionRef = useRef(null);
  const inView = useInView(sectionRef, { once: true, margin: "-15% 0px" });
  const { member } = useMember();
  const [config, setConfig] = useState(null);
  const [state, setState] = useState(null); // {current, lifetime_earned, ...}

  useEffect(() => {
    api.get("/loyalty/config").then(({ data }) => setConfig(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!member) return;
    memberApi.get("/member/loyalty").then(({ data }) => setState(data)).catch(() => {});
  }, [member?.id]);

  if (config && config.is_visible === false) return null;
  const target = config?.purchases_required || 6;
  const current = state?.current;
  const progress = current?.progress || 0;
  const completed = current?.status === "completed";

  return (
    <section ref={sectionRef} data-testid="home-loyalty-journey" className="relative overflow-hidden py-24 lg:py-32 bg-brand-cream">
      {/* Ambient background */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div className="absolute -top-20 -right-20 w-[440px] h-[440px] rounded-full bg-brand-yellow/25 blur-3xl" animate={{ x: [0, 40, 0], y: [0, 20, 0] }} transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }} />
        <motion.div className="absolute bottom-0 left-0 w-[380px] h-[380px] rounded-full bg-[#7EC8E3]/20 blur-3xl" animate={{ x: [0, 30, 0], y: [0, -20, 0] }} transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }} />
        {/* Bamboo leaves */}
        {["🎋", "🍭", "✨"].map((e, i) => (
          <motion.span key={i} className="absolute select-none text-3xl opacity-30" style={{ left: `${8 + i * 30}%`, top: `${(i % 2) * 60 + 8}%` }} animate={{ y: [-10, 10, -10], rotate: [-8, 8, -8] }} transition={{ duration: 6 + i, repeat: Infinity }}>{e}</motion.span>
        ))}
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-14">
          <motion.div initial={{ y: -8, opacity: 0 }} animate={inView ? { y: 0, opacity: 1 } : {}} className="text-[11px] uppercase tracking-[0.32em] text-brand-charcoal/55 inline-flex items-center gap-2">
            <Sparkles size={13} /> Popsicle Loyalty Journey
          </motion.div>
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            animate={inView ? { y: 0, opacity: 1 } : {}}
            transition={{ delay: 0.1 }}
            className="editorial-h1 text-5xl lg:text-6xl mt-3"
          >
            Buy {target} popsicles.<br />
            <span style={{ background: "linear-gradient(90deg, #FFC93C, #FF4F5A, #FFC93C)", backgroundSize: "200% 100%", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", animation: "ppGradShiftL 4s ease-in-out infinite" }}>Unlock 1 free.</span>
          </motion.h2>
          <motion.p initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ delay: 0.2 }} className="mt-4 text-brand-charcoal/70 max-w-2xl mx-auto">
            Every {config?.reward_label || "popsicle"} purchase advances the panda along a bamboo trail. Complete the journey and claim your reward at any participating Pop Panda Go store — the trail begins again immediately for another round of icy joy.
          </motion.p>
        </div>

        {/* Trail card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.3 }}
          className="relative rounded-[36px] p-8 lg:p-12 bg-white shadow-[0_30px_80px_-20px_rgba(0,0,0,0.15)] border border-brand-line overflow-hidden"
        >
          <div className="absolute -top-10 -right-10 w-72 h-72 rounded-full bg-brand-yellow/20 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-16 -left-16 w-72 h-72 rounded-full bg-[#FF4F5A]/10 blur-3xl pointer-events-none" />

          <div className="relative grid lg:grid-cols-[1fr_auto] gap-8 items-center">
            <div className="flex flex-col items-center lg:items-start">
              {member && state ? (
                <>
                  <div className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Cycle {current?.cycle_number || 1} · Your Journey</div>
                  <div className="mt-1 font-display text-3xl">
                    {completed ? (
                      <span className="text-[#FF4F5A]">🎉 Reward ready to claim!</span>
                    ) : (
                      <>{progress} <span className="text-brand-charcoal/50">of</span> {target} popsicles</>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">The Path to a Free Popsicle</div>
                  <div className="mt-1 font-display text-3xl">Six popsicles. One free.</div>
                </>
              )}
              <div className="mt-6 w-full">
                <LoyaltyTrail progress={progress} target={target} completed={completed} variant="hero" />
              </div>
            </div>

            {/* Right — status card */}
            <div className="rounded-3xl bg-brand-charcoal text-brand-cream p-6 min-w-[260px]">
              {member ? (
                <>
                  <div className="text-[10px] uppercase tracking-widest text-brand-yellow">Your Panda Journey</div>
                  {completed ? (
                    <>
                      <div className="mt-3 font-display text-2xl">Free popsicle ready!</div>
                      <div className="mt-2 text-brand-cream/70 text-sm">Show your Membership QR at any Pop Panda Go store to claim.</div>
                      <div className="mt-4 rounded-xl bg-white/5 border border-white/10 p-3">
                        <div className="text-[10px] uppercase tracking-widest text-brand-cream/60">Reward Code</div>
                        <div className="mt-1 font-mono text-brand-yellow tracking-widest" data-testid="loyalty-hero-reward-code">{current?.reward_code}</div>
                      </div>
                      <Link to="/member/dashboard" data-testid="loyalty-hero-view" className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand-yellow text-brand-charcoal px-4 py-2 text-sm hover:brightness-95">
                        View my journey <ArrowRight size={14} />
                      </Link>
                    </>
                  ) : (
                    <>
                      <div className="mt-3 font-display text-2xl">{target - progress} to go</div>
                      <div className="mt-2 text-brand-cream/70 text-sm">Every popsicle purchase advances your journey. Show your Membership QR at checkout.</div>
                      <Link to="/member/dashboard" data-testid="loyalty-hero-view" className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand-yellow text-brand-charcoal px-4 py-2 text-sm hover:brightness-95">
                        View my journey <ArrowRight size={14} />
                      </Link>
                    </>
                  )}
                  <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                    <MiniStat label="Earned" value={state?.lifetime_earned || 0} />
                    <MiniStat label="Redeemed" value={state?.lifetime_redeemed || 0} />
                  </div>
                </>
              ) : (
                <>
                  <div className="text-[10px] uppercase tracking-widest text-brand-yellow">Members-only</div>
                  <div className="mt-3 font-display text-2xl">Log in to start</div>
                  <div className="mt-2 text-brand-cream/70 text-sm">Panda Club members earn a free popsicle after every {target} purchases. Automatic tracking via your Membership QR.</div>
                  <div className="mt-5 flex flex-col gap-2">
                    <Link to="/member/register" data-testid="loyalty-hero-join" className="rounded-full bg-brand-yellow text-brand-charcoal px-4 py-2 text-sm text-center hover:brightness-95">Join Panda Club — Free</Link>
                    <Link to="/member/login" data-testid="loyalty-hero-signin" className="rounded-full border border-white/25 px-4 py-2 text-sm text-center hover:bg-white/10 inline-flex items-center justify-center gap-1">
                      <Lock size={12} /> Sign in
                    </Link>
                  </div>
                </>
              )}
            </div>
          </div>
        </motion.div>

        {/* How it works */}
        <div className="mt-14 grid sm:grid-cols-3 gap-4">
          {[
            { title: "Buy popsicles", desc: `Every purchase at a participating Pop Panda Go store advances your journey.`, icon: "🍭" },
            { title: `Complete the ${target}-stop trail`, desc: "Watch the little panda walk across the bamboo path with every checkout.", icon: "🎋" },
            { title: "Claim your free popsicle", desc: `Show your Membership QR — staff marks it redeemed and a fresh journey begins.`, icon: "🎁" },
          ].map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.5 + i * 0.1 }}
              className="rounded-2xl bg-white/70 backdrop-blur-lg border border-brand-line p-5"
            >
              <div className="text-3xl">{s.icon}</div>
              <div className="mt-2 font-display text-lg">{s.title}</div>
              <div className="mt-1 text-sm text-brand-charcoal/65">{s.desc}</div>
            </motion.div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes ppGradShiftL { 0%, 100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }
      `}</style>
    </section>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-lg bg-white/5 p-2">
      <div className="text-[9px] uppercase tracking-widest text-brand-cream/60">{label}</div>
      <div className="font-display text-lg">{value}</div>
    </div>
  );
}
